import { useEffect, useMemo, useState } from 'react';
import {
  BoutonPrincipal,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { BandeauExpedition } from '../components/notifications/BandeauExpedition';
import { usePerimetre } from '../site/usePerimetre';
import { useTaillePage } from '../lib/useTaillePage';
import {
  useEnvoyerRelances,
  useModifierScenario,
  useRelances,
  type CadenceRelance,
  type CanalNotification,
  type CibleRelance,
  type DestinatairesRelance,
  type ScenarioRelance,
} from '../lib/useRelances';
import { ErreurApi } from '../lib/api';

function horodatage(valeur: string): string {
  return new Date(valeur).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

const LIBELLES_CANAL: Record<CanalNotification, string> = {
  EMAIL: 'Mail',
  SMS: 'SMS',
  IN_APP: 'Appli',
};

/**
 * Écran « Relances automatiques » du pôle commercial (CDC §5.5).
 *
 * Cinq scénarios, dont quatre calculables avec les données disponibles. Le
 * nombre de destinataires affiché est celui que le scénario relancerait
 * maintenant : ce n'est pas une estimation.
 *
 * Ce qui part réellement dépend des canaux raccordés côté serveur : le bandeau
 * d'expédition le dit avant qu'on appuie sur le bouton, et l'historique donne
 * l'issue canal par canal — un mail parti et un SMS seulement tracé sur le même
 * destinataire sont deux lignes distinctes.
 *
 * Chaque scénario se règle ici : activation, destinataires (sa cible, le
 * responsable du dossier, ou les deux), fréquence, canaux et messages. La
 * fréquence est validée et énoncée par le serveur : l'écran envoie le réglage
 * et affiche le libellé rendu, il ne recalcule rien.
 */
export function ComRelancesPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useRelances(site);
  const modifier = useModifierScenario();
  const envoyer = useEnvoyerRelances();

  const compteurs = data?.compteurs;
  const expedition = data?.expedition;
  const derniersEnvois = useMemo(() => data?.derniersEnvois ?? [], [data?.derniersEnvois]);

  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();

  useEffect(() => {
    setPage(1);
  }, [site, taillePage]);

  const totalEnvois = derniersEnvois.length;
  const pagesEnvois = Math.max(1, Math.ceil(totalEnvois / taillePage));
  const envoisAffiches = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return derniersEnvois.slice(debut, debut + taillePage);
  }, [derniersEnvois, page, taillePage]);

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Relances automatiques"
        sousTitre="Scénarios mail et SMS pour les candidats et apprenants inactifs"
      />

      <BarreSites />

      <BandeauExpedition
        etat={expedition}
        contexte="Le circuit reste vérifiable de bout en bout : chaque relance est tracée avec son destinataire réel et son message final."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Relances envoyées (30 j)"
          valeur={compteurs?.envois30Jours ?? 0}
          detail={compteurs ? `${compteurs.mails} mails · ${compteurs.sms} SMS` : undefined}
          ton="navy"
        />
        <Compteur
          libelle="Mails envoyés (30 j)"
          valeur={compteurs?.mails ?? 0}
          ton="mint"
        />
        <Compteur
          libelle="Dossiers débloqués"
          valeur={compteurs?.ciblesEnAttente ?? 0}
          detail="après relance"
          ton="blue"
        />
        <Compteur
          libelle="Sans réponse"
          valeur={compteurs?.sansCoordonnees ?? 0}
          detail="escalade au conseiller"
          ton="warm"
        />
      </div>

      {(modifier.error instanceof ErreurApi || envoyer.error instanceof ErreurApi) && (
        <MessageErreur
          message={
            (modifier.error instanceof ErreurApi && modifier.error.message) ||
            (envoyer.error instanceof ErreurApi && envoyer.error.message) ||
            'Action refusée.'
          }
        />
      )}

      {envoyer.data && (
        <div className="space-y-2 rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          <p>{envoyer.data.message}</p>
          {envoyer.data.ecartes.length > 0 && (
            <div className="text-[11px]">
              {envoyer.data.ecartes.length} destinataire(s) écarté(s) :
              <ul className="mt-1 list-inside list-disc">
                {envoyer.data.ecartes.map((ecarte) => (
                  <li key={ecarte.nom}>
                    {ecarte.nom} — {ecarte.motif}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Un refus du prestataire n'est pas un destinataire écarté : le
              message était joignable, c'est l'expédition qui a échoué. Le
              distinguer évite de chercher une coordonnée manquante qui
              n'existe pas. */}
          {envoyer.data.echecs.length > 0 && (
            <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[11px] text-red-700">
              {/* Pictogramme en SVG inline, comme partout ailleurs dans le
                  projet : `lucide-react` n'est pas une dépendance du front, et
                  l'ajouter pour une seule icône ne se justifie pas. */}
              <svg
                className="mt-0.5 h-4 w-4 shrink-0"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              <div>
                {envoyer.data.echecs.length} envoi(s) refusé(s) par le prestataire :
                <ul className="mt-1 list-inside list-disc">
                  {envoyer.data.echecs.map((echec) => (
                    <li key={echec.nom}>
                      {echec.nom} — {echec.motif}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Carte titre="Scénarios actifs" sansBordure>
          {isLoading && <Chargement libelle="Chargement des scénarios…" />}
          {error && (
            <div className="p-4 sm:p-6">
              <MessageErreur
                message={
                  error instanceof ErreurApi
                    ? error.message
                    : 'Les scénarios n’ont pas pu être chargés.'
                }
              />
            </div>
          )}
          <div className="divide-y divide-gray-100">
            {(data?.scenarios && data.scenarios.length > 0) ? (
              data.scenarios.map((scenario) => (
                <LigneScenario
                  key={scenario.code}
                  scenario={scenario}
                  enCours={modifier.isPending}
                  onModifier={(valeurs) => modifier.mutate({ code: scenario.code, ...valeurs })}
                />
              ))
            ) : (
              <p className="px-4 sm:px-6 py-4 text-sm text-codialis-gray">
                Aucun scénario de relance configuré.
              </p>
            )}
          </div>
        </Carte>

        <div className="space-y-6">
          <FormulaireEnvoi
            scenarios={(data?.scenarios ?? []).filter((scenario) => scenario.calculable)}
            variables={data?.variables ?? []}
            enCours={envoyer.isPending}
            onEnvoyer={(entree) => envoyer.mutate({ ...entree, site })}
          />

          <Carte titre="Derniers envois" sansBordure>
            <div className="divide-y divide-gray-100">
              {totalEnvois > 0 ? (
                envoisAffiches.map((envoi) => (
                  <div key={envoi.id} className="px-4 sm:px-6 py-3">
                    <div className="flex items-center gap-2">
                      <Pastille
                        texte={LIBELLES_CANAL[envoi.canal]}
                        ton={envoi.canal === 'SMS' ? 'mint' : 'info'}
                      />
                      <span className="text-sm font-medium text-codialis-navy">{envoi.nom}</span>
                      <span className="ml-auto text-[11px] text-codialis-gray">
                        {envoi.siteCode}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-codialis-gray">
                      {envoi.pour === 'RESPONSABLE' ? 'Récapitulatif au responsable · ' : ''}
                      {envoi.destinataire ? `${envoi.destinataire} · ` : ''}
                      {horodatage(envoi.creeLe)}
                      {envoi.issue === 'JOURNALISE' ? ' · non expédié' : ''}
                    </div>
                    {envoi.message && (
                      <p className="mt-1 text-[11px] italic text-codialis-gray">{envoi.message}</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="px-4 sm:px-6 py-4 text-sm text-codialis-gray">Aucun envoi récent.</p>
              )}
            </div>
            {!isLoading && !error && totalEnvois > 0 && (
              <Pagination
                page={page}
                pages={pagesEnvois}
                total={totalEnvois}
                affiches={envoisAffiches.length}
                taillePage={taillePage}
                onPage={setPage}
                onChangerTaille={setTaillePage}
                uniteLabel="envoi"
              />
            )}
          </Carte>
        </div>
      </div>
    </div>
  );
}

/** Réglage envoyé au serveur depuis une ligne de scénario. */
interface ReglageScenario {
  actif?: boolean;
  destinataires?: DestinatairesRelance;
  cadence?: CadenceRelance;
  canaux?: CanalNotification[];
  message?: string;
  messageResponsable?: string;
}

const CLASSE_LIBELLE =
  'mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy';
const CLASSE_CHAMP =
  'w-full min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none';

function LigneScenario({
  scenario,
  enCours,
  onModifier,
}: {
  scenario: ScenarioRelance;
  enCours: boolean;
  onModifier: (valeurs: ReglageScenario) => void;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const versCible = scenario.destinataires !== 'RESPONSABLE';
  const versResponsable = scenario.destinataires !== 'CIBLE';

  function basculerCanal(canal: CanalNotification): void {
    const actuels = new Set(scenario.canaux);
    if (actuels.has(canal)) actuels.delete(canal);
    else actuels.add(canal);
    if (actuels.size === 0) return;
    onModifier({ canaux: [...actuels] });
  }

  return (
    <div className="px-4 sm:px-6 py-4">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => onModifier({ actif: !scenario.actif })}
          disabled={enCours}
          aria-pressed={scenario.actif}
          aria-label={`${scenario.actif ? 'Désactiver' : 'Activer'} ${scenario.libelle}`}
          className={`flex h-6 w-10 shrink-0 items-center rounded-full px-1 transition disabled:opacity-50 ${
            scenario.actif ? 'bg-codialis-teal' : 'bg-gray-300'
          }`}
        >
          <span
            className="h-4 w-4 rounded-full bg-white transition"
            style={{ transform: `translateX(${scenario.actif ? '18px' : '0'})` }}
          />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-codialis-navy">{scenario.libelle}</div>
          <div className="text-[11px] text-codialis-gray">
            {scenario.declencheur} · {scenario.destinataire}
          </div>
        </div>
        <div className="shrink-0 text-right">
          {scenario.calculable ? (
            <div className="text-xs text-codialis-gray">{scenario.cibles} concerné(s)</div>
          ) : (
            <Pastille texte="Données phase 3" ton="warning" />
          )}
          {versResponsable && scenario.sansResponsable > 0 && (
            <div className="text-[10px] font-semibold text-amber-700">
              {scenario.sansResponsable} sans responsable
            </div>
          )}
          <div className="text-[10px] text-codialis-gray">{scenario.envois} envoi(s) · 30 j</div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {versCible &&
          (['EMAIL', 'SMS'] as const).map((canal) => (
            <button
              key={canal}
              type="button"
              onClick={() => basculerCanal(canal)}
              disabled={enCours}
              aria-pressed={scenario.canaux.includes(canal)}
              aria-label={`${LIBELLES_CANAL[canal]} — ${scenario.cible.toLowerCase()}`}
              className={`rounded-lg border px-2 py-1 text-[11px] font-bold transition disabled:opacity-50 ${
                scenario.canaux.includes(canal)
                  ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                  : 'border-gray-200 bg-gray-50 text-codialis-gray'
              }`}
            >
              {LIBELLES_CANAL[canal]}
            </button>
          ))}
        {versResponsable && (
          <span className="rounded-lg bg-gray-50 px-2 py-1 text-[11px] text-codialis-gray">
            Responsable : mail récapitulatif
          </span>
        )}
        <button
          type="button"
          onClick={() => setOuvert((valeur) => !valeur)}
          aria-expanded={ouvert}
          className="text-[11px] font-semibold text-codialis-sky hover:underline"
        >
          {ouvert ? 'Masquer le réglage' : 'Paramétrer'}
        </button>
      </div>

      {ouvert && (
        <div className="mt-3 space-y-4 rounded-2xl bg-codialis-sand p-3 sm:p-4">
          <ChoixDestinataires scenario={scenario} enCours={enCours} onModifier={onModifier} />
          {scenario.cadence ? (
            <EditeurCadence
              // Repart du réglage enregistré dès que le serveur en renvoie un autre.
              key={JSON.stringify(scenario.cadence)}
              code={scenario.code}
              cadence={scenario.cadence}
              evenement={scenario.evenement}
              enCours={enCours}
              onEnregistrer={(cadence) => onModifier({ cadence })}
            />
          ) : (
            <p className="text-xs text-codialis-gray">
              Fréquence : {scenario.declencheur}. Elle se réglera quand la plateforme calculera
              ce scénario.
            </p>
          )}
          <EditeurMessages scenario={scenario} enCours={enCours} onModifier={onModifier} />
        </div>
      )}
    </div>
  );
}

function ChoixDestinataires({
  scenario,
  enCours,
  onModifier,
}: {
  scenario: ScenarioRelance;
  enCours: boolean;
  onModifier: (valeurs: ReglageScenario) => void;
}): JSX.Element {
  const choix: { valeur: DestinatairesRelance; libelle: string }[] = [
    { valeur: 'CIBLE', libelle: scenario.cible },
    { valeur: 'RESPONSABLE', libelle: 'Responsable du dossier' },
    { valeur: 'LES_DEUX', libelle: 'Les deux' },
  ];

  return (
    <fieldset>
      <legend className={CLASSE_LIBELLE}>Destinataires</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {choix.map(({ valeur, libelle }) => (
          <button
            key={valeur}
            type="button"
            disabled={enCours}
            aria-pressed={scenario.destinataires === valeur}
            onClick={() => {
              if (scenario.destinataires !== valeur) onModifier({ destinataires: valeur });
            }}
            className={`min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] min-w-0 rounded-xl border px-3 py-1.5 text-xs font-semibold transition disabled:opacity-50 ${
              scenario.destinataires === valeur
                ? 'border-codialis-mint bg-white text-codialis-navy'
                : 'border-gray-200 bg-gray-50 text-codialis-gray'
            }`}
          >
            {libelle}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[10px] text-codialis-gray">
        Le responsable est le conseiller référent du dossier. Sans responsable en charge, la
        relance part au pôle administratif de l’antenne. Il reçoit un mail récapitulatif par
        passage, une ligne par dossier.
      </p>
    </fieldset>
  );
}

function ChampNombre({
  id,
  libelle,
  valeur,
  min,
  max,
  onChange,
}: {
  id: string;
  libelle: string;
  valeur: number;
  min: number;
  max: number;
  onChange: (valeur: number) => void;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-[11px] text-codialis-gray">
        {libelle}
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={Number.isNaN(valeur) ? '' : valeur}
        onChange={(evenement) => onChange(Number.parseInt(evenement.target.value, 10))}
        className={CLASSE_CHAMP}
      />
    </div>
  );
}

function ChampHeure({
  id,
  libelle,
  valeur,
  onChange,
}: {
  id: string;
  libelle: string;
  valeur: number;
  onChange: (valeur: number) => void;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-[11px] text-codialis-gray">
        {libelle}
      </label>
      <select
        id={id}
        value={valeur}
        onChange={(evenement) => onChange(Number(evenement.target.value))}
        className={CLASSE_CHAMP}
      >
        {HEURES.map((heure) => (
          <option key={heure} value={heure}>
            {heure} h
          </option>
        ))}
      </select>
    </div>
  );
}

const HEURES = Array.from({ length: 24 }, (_, heure) => heure);

function entierDans(valeur: number, min: number, max: number): boolean {
  return Number.isInteger(valeur) && valeur >= min && valeur <= max;
}

/** Bornes reprises de la validation du serveur, pour griser l'enregistrement plutôt qu'attendre un refus. */
function cadenceValide(cadence: CadenceRelance): boolean {
  switch (cadence.type) {
    case 'CRENEAUX':
      return (
        cadence.heures.length > 0 &&
        entierDans(cadence.joursSoutenus, 0, 60) &&
        entierDans(cadence.periodeEspaceeJours, 0, 30) &&
        (cadence.joursSoutenus > 0 || cadence.periodeEspaceeJours > 0)
      );
    case 'PERIODIQUE':
      return entierDans(cadence.delaiHeures, 1, 720) && entierDans(cadence.periodeJours, 0, 90);
    case 'AVANT':
      return entierDans(cadence.jours, 0, 30);
    case 'APRES':
      return entierDans(cadence.jours, 0, 60);
  }
}

/**
 * Réglage de la fréquence, sous la forme propre au scénario. L'enregistrement
 * renvoie la cadence au serveur, qui la valide et en rend le libellé.
 */
function EditeurCadence({
  code,
  cadence: initiale,
  evenement,
  enCours,
  onEnregistrer,
}: {
  code: string;
  cadence: CadenceRelance;
  evenement: string;
  enCours: boolean;
  onEnregistrer: (cadence: CadenceRelance) => void;
}): JSX.Element {
  const [cadence, setCadence] = useState<CadenceRelance>(initiale);
  const modifiee = JSON.stringify(cadence) !== JSON.stringify(initiale);
  const valide = cadenceValide(cadence);
  const prefixe = `cadence-${code}`;

  function basculerHeure(heure: number): void {
    if (cadence.type !== 'CRENEAUX') return;
    const heures = cadence.heures.includes(heure)
      ? cadence.heures.filter((valeur) => valeur !== heure)
      : [...cadence.heures, heure].sort((a, b) => a - b);
    if (heures.length > 6) return;
    setCadence({ ...cadence, heures });
  }

  return (
    <fieldset>
      <legend className={CLASSE_LIBELLE}>Fréquence</legend>

      {cadence.type === 'CRENEAUX' && (
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] text-codialis-gray">
              Heures d’envoi chaque jour de la période soutenue (six au plus)
            </div>
            {/* Colonnes réglées sur la largeur de la carte, pas sur celle de
                l'écran : à 1024 px la carte n'occupe qu'une demi-largeur, et
                douze colonnes y donnaient des boutons de 19 px. */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(48px,1fr))] gap-1">
              {HEURES.map((heure) => (
                <button
                  key={heure}
                  type="button"
                  aria-pressed={cadence.heures.includes(heure)}
                  onClick={() => basculerHeure(heure)}
                  className={`min-h-[32px] [@media(pointer:coarse)]:min-h-[40px] whitespace-nowrap rounded-lg border text-[11px] font-semibold transition ${
                    cadence.heures.includes(heure)
                      ? 'border-codialis-mint bg-white text-codialis-navy'
                      : 'border-gray-200 bg-gray-50 text-codialis-gray'
                  }`}
                >
                  {heure} h
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <ChampNombre
              id={`${prefixe}-soutenus`}
              libelle={`Pendant (jours, dès le lendemain de ${evenement})`}
              valeur={cadence.joursSoutenus}
              min={0}
              max={60}
              onChange={(joursSoutenus) => setCadence({ ...cadence, joursSoutenus })}
            />
            <ChampNombre
              id={`${prefixe}-espace`}
              libelle="Puis un mail tous les (jours, 0 : arrêt)"
              valeur={cadence.periodeEspaceeJours}
              min={0}
              max={30}
              onChange={(periodeEspaceeJours) => setCadence({ ...cadence, periodeEspaceeJours })}
            />
            <ChampHeure
              id={`${prefixe}-heure-espacee`}
              libelle="À"
              valeur={cadence.heureEspacee}
              onChange={(heureEspacee) => setCadence({ ...cadence, heureEspacee })}
            />
          </div>
        </div>
      )}

      {cadence.type === 'PERIODIQUE' && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ChampNombre
            id={`${prefixe}-delai`}
            libelle={`Premier mail (heures après ${evenement})`}
            valeur={cadence.delaiHeures}
            min={1}
            max={720}
            onChange={(delaiHeures) => setCadence({ ...cadence, delaiHeures })}
          />
          <ChampNombre
            id={`${prefixe}-periode`}
            libelle="Puis tous les (jours, 0 : mail unique)"
            valeur={cadence.periodeJours}
            min={0}
            max={90}
            onChange={(periodeJours) => setCadence({ ...cadence, periodeJours })}
          />
        </div>
      )}

      {(cadence.type === 'AVANT' || cadence.type === 'APRES') && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ChampNombre
            id={`${prefixe}-jours`}
            libelle={`Jours ${cadence.type === 'AVANT' ? 'avant' : 'après'} ${evenement} (0 : le jour même)`}
            valeur={cadence.jours}
            min={0}
            max={cadence.type === 'AVANT' ? 30 : 60}
            onChange={(jours) => setCadence({ ...cadence, jours })}
          />
          <ChampHeure
            id={`${prefixe}-heure`}
            libelle="À"
            valeur={cadence.heure}
            onChange={(heure) => setCadence({ ...cadence, heure })}
          />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onEnregistrer(cadence)}
          disabled={enCours || !modifiee || !valide}
          className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg bg-codialis-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-50"
        >
          Enregistrer la fréquence
        </button>
        {modifiee && (
          <button
            type="button"
            onClick={() => setCadence(initiale)}
            className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-codialis-navy transition hover:bg-gray-50"
          >
            Annuler
          </button>
        )}
        {!valide && (
          <span className="text-[11px] text-red-700">
            Fréquence incomplète : vérifiez les valeurs saisies.
          </span>
        )}
      </div>
    </fieldset>
  );
}

function EditeurMessages({
  scenario,
  enCours,
  onModifier,
}: {
  scenario: ScenarioRelance;
  enCours: boolean;
  onModifier: (valeurs: ReglageScenario) => void;
}): JSX.Element {
  const [message, setMessage] = useState(scenario.message);
  const [messageResponsable, setMessageResponsable] = useState(scenario.messageResponsable);
  const versCible = scenario.destinataires !== 'RESPONSABLE';
  const versResponsable = scenario.destinataires !== 'CIBLE';

  const changements: ReglageScenario = {
    ...(message.trim() !== scenario.message ? { message: message.trim() } : {}),
    ...(messageResponsable.trim() !== scenario.messageResponsable
      ? { messageResponsable: messageResponsable.trim() }
      : {}),
  };
  const valides = message.trim().length >= 10 && messageResponsable.trim().length >= 10;

  return (
    <fieldset className="space-y-3">
      <legend className={CLASSE_LIBELLE}>Messages</legend>
      <div>
        <label
          htmlFor={`message-${scenario.code}`}
          className="mb-1 block text-[11px] text-codialis-gray"
        >
          Message — {scenario.cible.toLowerCase()}
          {!versCible && ' (non envoyé avec le réglage actuel)'}
        </label>
        <textarea
          id={`message-${scenario.code}`}
          rows={3}
          value={message}
          onChange={(evenement) => setMessage(evenement.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
        />
        <p className="mt-1 text-[10px] text-codialis-gray">
          Variables : {scenario.variables.map((variable) => `{{${variable}}}`).join(' · ')}
        </p>
      </div>
      <div>
        <label
          htmlFor={`message-responsable-${scenario.code}`}
          className="mb-1 block text-[11px] text-codialis-gray"
        >
          Ligne du récapitulatif — responsable du dossier
          {!versResponsable && ' (non envoyée avec le réglage actuel)'}
        </label>
        <textarea
          id={`message-responsable-${scenario.code}`}
          rows={3}
          value={messageResponsable}
          onChange={(evenement) => setMessageResponsable(evenement.target.value)}
          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm"
        />
        <p className="mt-1 text-[10px] text-codialis-gray">
          Variables :{' '}
          {scenario.variablesResponsable.map((variable) => `{{${variable}}}`).join(' · ')}
        </p>
      </div>
      <button
        type="button"
        onClick={() => onModifier(changements)}
        disabled={enCours || !valides || Object.keys(changements).length === 0}
        className="min-h-[36px] [@media(pointer:coarse)]:min-h-[40px] rounded-lg bg-codialis-navy px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-50"
      >
        Enregistrer les messages
      </button>
    </fieldset>
  );
}

function FormulaireEnvoi({
  scenarios,
  variables,
  enCours,
  onEnvoyer,
}: {
  scenarios: ScenarioRelance[];
  variables: string[];
  enCours: boolean;
  onEnvoyer: (entree: {
    cible: CibleRelance;
    canaux: CanalNotification[];
    message?: string;
  }) => void;
}): JSX.Element {
  const [cible, setCible] = useState<CibleRelance | string>('');
  const [canaux, setCanaux] = useState<CanalNotification[]>(['EMAIL', 'SMS']);
  const [message, setMessage] = useState('');

  const scenarioChoisi = scenarios.find((scenario) => scenario.code === cible) ?? null;
  // Un scénario réglé pour le seul responsable n'emploie ni les canaux ni le
  // message de la cible : il envoie le récapitulatif par mail.
  const responsableSeul = scenarioChoisi?.destinataires === 'RESPONSABLE';

  useEffect(() => {
    if (scenarioChoisi || scenarios.length === 0) return;
    const premier = scenarios[0];
    if (!premier) return;
    setCible(premier.code);
    setMessage(premier.message);
    setCanaux(premier.canaux);
  }, [scenarios, scenarioChoisi]);

  return (
    <Carte titre="Composer une relance" sansBordure>
      <form
        className="space-y-3 p-4 sm:p-6"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (!scenarioChoisi || enCours) return;
          if (!responsableSeul && canaux.length === 0) return;
          onEnvoyer({
            cible: scenarioChoisi.code as CibleRelance,
            // Le serveur exige un canal ; le récapitulatif au responsable part par mail.
            canaux: responsableSeul ? ['EMAIL'] : canaux,
            ...(!responsableSeul && message.trim().length >= 10 ? { message: message.trim() } : {}),
          });
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <fieldset className={responsableSeul ? 'hidden' : undefined}>
            <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Canal
            </legend>
            <div className="flex gap-2">
              {(['EMAIL', 'SMS'] as const).map((canal) => (
                <button
                  key={canal}
                  type="button"
                  aria-pressed={canaux.includes(canal)}
                  onClick={() =>
                    setCanaux((liste) =>
                      liste.includes(canal)
                        ? liste.filter((valeur) => valeur !== canal)
                        : [...liste, canal],
                    )
                  }
                  className={`min-w-0 flex-1 rounded-xl border py-2 text-sm font-semibold transition ${
                    canaux.includes(canal)
                      ? 'border-codialis-mint bg-codialis-sand text-codialis-navy'
                      : 'border-gray-200 bg-gray-50 text-codialis-gray'
                  }`}
                >
                  {LIBELLES_CANAL[canal]}
                </button>
              ))}
            </div>
          </fieldset>
          <div>
            <label htmlFor="comrelances-cible" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Cible
            </label>
            <select id="comrelances-cible"
              value={cible}
              onChange={(evenement) => {
                const valeur = evenement.target.value;
                setCible(valeur);
                const scenario = scenarios.find((entree) => entree.code === valeur);
                if (scenario) {
                  setMessage(scenario.message);
                  setCanaux(scenario.canaux);
                }
              }}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
            >
              {scenarios.length > 0 ? (
                scenarios.map((scenario) => (
                  <option key={scenario.code} value={scenario.code}>
                    {scenario.libelle} ({scenario.cibles})
                  </option>
                ))
              ) : (
                <option value="">Aucun scénario disponible</option>
              )}
            </select>
          </div>
        </div>

        {responsableSeul && (
          <p className="rounded-xl bg-codialis-sand px-3 py-2 text-xs text-codialis-navy">
            Ce scénario s’adresse au responsable de chaque dossier : un mail récapitulatif lui
            part, une ligne par dossier. Le message et les canaux se règlent avec « Paramétrer ».
          </p>
        )}

        <div className={responsableSeul ? 'hidden' : undefined}>
          <label htmlFor="comrelances-message" className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
            Message
          </label>
          <textarea id="comrelances-message"
            rows={4}
            value={message}
            onChange={(evenement) => setMessage(evenement.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm"
            placeholder="Choisissez une cible pour partir du message du scénario"
          />
          {variables.length > 0 && (
            <p className="mt-1 text-[10px] text-codialis-gray">
              Variables : {variables.map((variable) => `{{${variable}}}`).join(' · ')}.
            </p>
          )}
        </div>

        <BoutonPrincipal
          type="submit"
          disabled={enCours || !scenarioChoisi || (!responsableSeul && canaux.length === 0)}
        >
          {enCours
            ? 'Préparation…'
            : scenarioChoisi
              ? `Relancer ${scenarioChoisi.cibles} concerné(s)`
              : 'Aucun scénario disponible'}
        </BoutonPrincipal>
      </form>
    </Carte>
  );
}
